import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CopyButton } from '@/components/ui/CopyButton';
import { buildCloudwatchLogsUrl, type DeploymentRecord } from '@/services/deploymentService';
import { getConfigValue } from '@/services/configService';
import { logStreamForTask, resolveDeploymentLogTarget, taskIdFromArn } from '@/services/logsService';
import type { FailureInfo } from '@/utils/deployFailureStore';
import type { PlanInfo } from '@/utils/deployPlanStore';
import { describePlanOutcome } from '@/utils/deployProgress';
import { isFinishedPlan, isGroupSummary, statusKey, waitPhrase } from '@/utils/deployStatus';
import { formatNzDateTime } from '@/utils/relativeTime';
import { classifyDeployFailure } from '../../../../lib/deploy-failure';
import './deployments.css';

interface RunDetailsProps {
  record: DeploymentRecord;
  /** Why the run failed. Absent for a run that did not fail. */
  failure?: FailureInfo;
  /** What a finished plan-only run found. Absent when it was not read. */
  plan?: PlanInfo;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="deploy-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** An id shown short, with the whole value in the tooltip and on the copy button. */
function Id({ value, short, copyLabel }: { value: string; short?: string; copyLabel: string }) {
  return (
    <>
      <span className="mono" title={value}>
        {short || value}
      </span>
      <CopyButton value={value} label={copyLabel} />
    </>
  );
}

/**
 * The opened row: the whole error first, then what to do about it, then the
 * ids an engineer needs to follow the run into AWS.
 */
export function RunDetails({ record, failure, plan }: RunDetailsProps) {
  const region = getConfigValue('AWS_REGION') || 'us-east-1';
  const status = statusKey(record);
  const group = isGroupSummary(record);
  const showFailure = status === 'failed' || status === 'resolved';
  const verdict = failure?.failure;
  const taskId = taskIdFromArn(record.ecsTaskArn);
  const executionName = record.sfnExecutionArn?.split(':').pop();
  const firstTask = record.firstAttemptTaskArn;
  const firstTaskIsOther = firstTask && firstTask !== record.ecsTaskArn;
  // Why the first attempt failed, when the pipeline ran the deploy twice. The shared classifier
  // gives the class its plain title.
  const firstAttemptWhy = record.firstAttemptErrorSummary || '';
  const firstAttemptVerdict = firstAttemptWhy ? classifyDeployFailure(firstAttemptWhy) : null;
  const firstAttemptTitle =
    firstAttemptVerdict && firstAttemptVerdict.errorClass === record.firstAttemptErrorClass
      ? firstAttemptVerdict.title
      : '';
  const firstTaskLog = firstTaskIsOther
    ? buildCloudwatchLogsUrl(region, resolveDeploymentLogTarget(record).group, logStreamForTask(firstTask))
    : null;

  return (
    <div className="deploy-details">
      {showFailure && (
        <div className="deploy-details__failure">
          {(!failure || failure.state === 'loading') && (
            <p className="deploy-details__note">
              <Spinner as="span" animation="border" size="sm" className="deploy-reason__spinner" /> Reading the log for
              the error
            </p>
          )}
          {verdict && (
            <>
              <h3 className="deploy-details__title">{verdict.title}</h3>
              <p className="deploy-details__advice">
                {verdict.advice}
                {verdict.retry === 'wait' && waitPhrase(verdict.waitSeconds)
                  ? ` Wait before you run it again${waitPhrase(verdict.waitSeconds)}.`
                  : ''}
              </p>
            </>
          )}
          {failure?.fromFirstAttempt && (
            <p className="deploy-details__note">
              This is why the first attempt failed. The pipeline ran the deploy a second time, and the second attempt
              failed with no error the pipeline could read. Open the log to see where it stopped.
            </p>
          )}
          {failure?.note && <p className="deploy-details__note">{failure.note}</p>}
          {failure?.blocksState === 'pending' && (
            <p className="deploy-details__note">
              <Spinner as="span" animation="border" size="sm" className="deploy-reason__spinner" /> Reading the log for
              the full error
            </p>
          )}
          {failure?.blocks.map((block, index) => (
            <pre key={index} className="deploy-error-block">
              {block}
            </pre>
          ))}
          {failure?.state === 'ready' && failure.source === 'log' && failure.blocks.length > 1 && (
            <p className="deploy-details__note">
              The log holds {failure.blocks.length} errors, oldest first. The title is about one of them: read them all.
            </p>
          )}
        </div>
      )}

      {(firstAttemptWhy || record.firstAttemptErrorClass) && !failure?.fromFirstAttempt && (
        <p className="deploy-details__resolved">
          First attempt: {firstAttemptTitle ? `${firstAttemptTitle}. ` : ''}
          {firstAttemptWhy || record.firstAttemptErrorClass}
        </p>
      )}

      {record.resolvedAt && (
        <p className="deploy-details__resolved">
          Resolved by {record.resolvedBy || 'somebody'} on {formatNzDateTime(record.resolvedAt)}
          {record.resolvedNote ? `: ${record.resolvedNote}` : '.'}
        </p>
      )}

      <dl className="deploy-facts">
        <Fact label={group ? 'Group run ID' : 'Deployment ID'}>
          <Id value={record.deploymentId} copyLabel="Copy ID" />
        </Fact>
        {record.ecsTaskArn && taskId && (
          <Fact label="Task">
            <Id value={record.ecsTaskArn} short={taskId} copyLabel="Copy task ARN" />
          </Fact>
        )}
        {record.sfnExecutionArn && executionName && (
          <Fact label="Execution">
            <Id value={record.sfnExecutionArn} short={executionName} copyLabel="Copy execution ARN" />
          </Fact>
        )}
        <Fact label="Started">{formatNzDateTime(record.startedAt) || 'Unknown'}</Fact>
        <Fact label="Ended">{record.endedAt ? formatNzDateTime(record.endedAt) : 'Not yet'}</Fact>
        <Fact label="Started by">{record.initiatedBy || 'Unknown'}</Fact>
        {(record.attemptNumber ?? 1) > 1 && (
          <Fact label="Attempts">{record.attemptNumber} (the pipeline ran it again by itself)</Fact>
        )}
        {firstTaskIsOther && firstTask && (
          <Fact label="First attempt task">
            <Id value={firstTask} short={taskIdFromArn(firstTask)} copyLabel="Copy first attempt task ARN" />
            <Link
              className="deploy-fact__link"
              to={`/deployments/${encodeURIComponent(record.deploymentId)}/logs?task=${encodeURIComponent(taskIdFromArn(firstTask) || '')}`}
            >
              Its log
            </Link>
            {firstTaskLog && (
              <a className="deploy-fact__link" href={firstTaskLog} target="_blank" rel="noopener noreferrer">
                In CloudWatch
              </a>
            )}
          </Fact>
        )}
        {record.mode === 'plan' && <Fact label="Mode">Plan only: nothing was changed</Fact>}
        {isFinishedPlan(record) && (
          <Fact label="Plan result">
            {plan?.state === 'loading'
              ? 'Reading the log'
              : describePlanOutcome(plan?.outcome) || "The plan's result is in the log"}
          </Fact>
        )}
        {record.message && !showFailure && <Fact label="Note">{record.message}</Fact>}
      </dl>
    </div>
  );
}

export default RunDetails;
